import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { LocalStorage } from '@/app/core/local-storage/local-storage';
import { environment } from '@/environments/environment';

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  admin: boolean;
}

interface LoginResponse {
  accessToken: string;
  user: CurrentUser;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly storage = inject(LocalStorage);

  private readonly TOKEN_KEY = 'geoqi_token';
  private readonly USER_KEY = 'geoqi_user';

  readonly currentUser = signal<CurrentUser | null>(this.loadUser());

  private loadUser(): CurrentUser | null {
    const raw = this.storage.getItem(this.USER_KEY);
    return raw ? JSON.parse(raw) : null;
  }

  getToken(): string | null {
    return this.storage.getItem(this.TOKEN_KEY);
  }

  login(email: string, password: string): Observable<void> {
    return this.http
      .post<LoginResponse>(`${environment.apiUrl}/auth/login`, {
        email,
        password,
      })
      .pipe(
        tap((res) => {
          console.log('Login successful:', res);
          this.storage.setItem(this.TOKEN_KEY, res.accessToken);
          this.storage.setItem(this.USER_KEY, JSON.stringify(res.user));
          this.currentUser.set(res.user);
        }),
        map(() => undefined)
      );
  }

  register(name: string, email: string, password: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/users`, {
      name,
      email,
      password,
    });
  }

  logout(): void {
    this.storage.removeItem(this.TOKEN_KEY);
    this.storage.removeItem(this.USER_KEY);
    this.currentUser.set(null);
    this.router.navigateByUrl('/auth/entrar');
  }

  isLoggedIn(): boolean {
    return this.currentUser() !== null;
  }
}
